<script setup>
import { ref, onMounted, computed, nextTick } from 'vue'
import { generateToken } from '../utils/auth'
import { fetchUrlSources } from '../utils/common'

const emit = defineEmits(['next-step'])

// 状态变量
const token = ref('')
const loading = ref(false)
const taskStatus = ref('')
const sourcePrefix = ref('素材模板/图片')
const sourceUrls = ref([])
const currentTab = ref('erase') // erase, outpaint, stylize, inpaint

// 模型列表
const eraseModels = ref([])
const outpaintModels = ref([])
const stylizeModels = ref([])
const inpaintModels = ref([])

// 选中的模型
const selectedEraseModel = ref('')
const selectedOutpaintModel = ref('')
const selectedStylizeModel = ref('')
const selectedInpaintModel = ref('')

// 图片URL和预览
const imageUrl = ref('')
const imagePreview = ref('')
const maskImageUrl = ref('')
const maskImagePreview = ref('')

// 扩图参数
const outpaintParams = ref({
  width: 1024,
  height: 1024,
  outpaint_ratio: 1.5,
  useCustomSize: false
})

// 局部重绘参数
const inpaintPrompt = ref('')

// 结果图片
const resultImage = ref('')

const showMaskModal = ref(false)
const canvasRef = ref(null)
const isDrawing = ref(false)
const drawingContext = ref(null)
const originalImage = ref(null)

// 添加笔刷大小状态
const brushSize = ref(20)

// 添加新的状态变量
const imageDimensions = ref({ width: 0, height: 0 })

// 添加计算属性处理模型选择
const selectedModel = computed({
  get() {
    switch (currentTab.value) {
      case 'erase':
        return selectedEraseModel.value
      case 'outpaint':
        return selectedOutpaintModel.value
      case 'stylize':
        return selectedStylizeModel.value
      case 'inpaint':
        return selectedInpaintModel.value
      default:
        return ''
    }
  },
  set(value) {
    switch (currentTab.value) {
      case 'erase':
        selectedEraseModel.value = value
        break
      case 'outpaint':
        selectedOutpaintModel.value = value
        break
      case 'stylize':
        selectedStylizeModel.value = value
        break
      case 'inpaint':
        selectedInpaintModel.value = value
        break
    }
  }
})

// 添加计算属性处理当前模型列表
const currentModels = computed(() => {
  switch (currentTab.value) {
    case 'erase':
      return eraseModels.value
    case 'outpaint':
      return outpaintModels.value
    case 'stylize':
      return stylizeModels.value
    case 'inpaint':
      return inpaintModels.value
    default:
      return []
  }
})

// 获取token
async function getToken() {
  token.value = generateToken()
}

// 获取各类型模型列表
async function fetchAllModels() {
  const apis = [
    { url: '/mhapi/v1/erase/models', target: eraseModels },
    { url: '/mhapi/v1/outpaint/models', target: outpaintModels },
    { url: '/mhapi/v1/stylization/models', target: stylizeModels },
    { url: '/mhapi/v1/inpaint/models', target: inpaintModels }
  ]

  for (const api of apis) {
    try {
      const response = await fetch(api.url, {
        headers: {
          'Authorization': `Bearer ${token.value}`,
          'Content-Type': 'application/json'
        }
      })
      const data = await response.json()
      if (data.data && Array.isArray(data.data)) {
        api.target.value = data.data
      }
    } catch (error) {
      console.error(`获取模型列表失败 (${api.url}):`, error)
    }
  }
}

// 处理图片URL输入
async function handleImageInput() {
  if (!imageUrl.value) return
  
  try {
    imagePreview.value = imageUrl.value
    // 获取图片尺寸
    const img = new Image()
    img.onload = () => {
      imageDimensions.value = {
        width: img.width,
        height: img.height
      }
    }
    img.src = imageUrl.value
  } catch (error) {
    console.error('URL预览失败:', error)
    taskStatus.value = 'URL预览失败: ' + error.message
  }
}

// 加载素材库
async function loadSourceImages() {
  try {
    loading.value = true
    taskStatus.value = '正在加载素材库...'
    
    const urls = await fetchUrlSources(sourcePrefix.value)
    sourceUrls.value = urls
    taskStatus.value = `已加载 ${urls.length} 张素材图片`
  } catch (error) {
    console.error('加载素材库失败:', error)
    taskStatus.value = '加载素材库失败: ' + error.message
  } finally {
    loading.value = false
  }
}

// 选择素材图片
function selectSourceImage(url, type = 'main') {
  if (type === 'main') {
    imageUrl.value = url
    handleImageInput()
  } else if (type === 'mask') {
    maskImageUrl.value = url
    handleMaskInput()
  }
}

// 执行智能擦除
async function runErase() {
  if (!imageUrl.value || !maskImageUrl.value || !selectedEraseModel.value) {
    alert('请选择模型并上传图片和遮罩')
    return
  }

  loading.value = true
  taskStatus.value = '正在执行智能擦除...'
  resultImage.value = ''

  try {
    const response = await fetch('/mhapi/v1/erase/imgen', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        img_url: imageUrl.value,
        model_id: selectedEraseModel.value,
        mask_img_url: maskImageUrl.value
      })
    })

    const data = await response.json()
    if (data.task_id) {
      pollEraseResult(data.task_id)
    } else {
      taskStatus.value = '擦除失败: ' + data.error.details.join(', ')
      loading.value = false
    }
  } catch (error) {
    console.error('擦除失败:', error)
    taskStatus.value = '擦除失败: ' + error.message
    loading.value = false
  }
}

// 执行智能扩图
async function runOutpaint() {
  if (!imageUrl.value || !selectedOutpaintModel.value) {
    alert('请选择模型并上传图片')
    return
  }

  loading.value = true
  taskStatus.value = '正在执行智能扩图...'
  resultImage.value = ''

  try {
    const endpoint = outpaintParams.value.useCustomSize ? 
      '/mhapi/v1/outpaint/imgen_free' : 
      '/mhapi/v1/outpaint/imgen'

    const body = {
      model_id: selectedOutpaintModel.value,
      img_url: imageUrl.value
    }

    if (outpaintParams.value.useCustomSize) {
      body.width = outpaintParams.value.width
      body.height = outpaintParams.value.height
    } else {
      body.outpaint_ratio = outpaintParams.value.outpaint_ratio
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    const data = await response.json()
    if (data.task_id) {
      pollOutpaintResult(data.task_id)
    } else {
      taskStatus.value = '扩图失败: ' + data.error.details.join(', ')
      loading.value = false
    }
  } catch (error) {
    console.error('扩图失败:', error)
    taskStatus.value = '扩图失败: ' + error.message
    loading.value = false
  }
}

// 执行风格化
async function runStylize() {
  if (!imageUrl.value || !selectedStylizeModel.value) {
    alert('请选择模型并上传图片')
    return
  }

  loading.value = true
  taskStatus.value = '正在执行风格化...'
  resultImage.value = ''

  try {
    const response = await fetch('/mhapi/v1/stylization/imgen', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model_id: selectedStylizeModel.value,
        img_url: imageUrl.value
      })
    })

    const data = await response.json()
    if (data.task_id) {
      pollStylizeResult(data.task_id)
    } else {
      taskStatus.value = '风格化失败: ' + data.error.details.join(', ')
      loading.value = false
    }
  } catch (error) {
    console.error('风格化失败:', error)
    taskStatus.value = '风格化失败: ' + error.message
    loading.value = false
  }
}

// 执行局部重绘
async function runInpaint() {
  if (!imageUrl.value || !maskImageUrl.value || !selectedInpaintModel.value || !inpaintPrompt.value) {
    alert('请选择模型并上传图片、遮罩和提示词')
    return
  }

  loading.value = true
  taskStatus.value = '正在执行局部重绘...'
  resultImage.value = ''

  try {
    const response = await fetch('/mhapi/v1/inpaint/imgen', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        img_url: imageUrl.value,
        model_id: selectedInpaintModel.value,
        mask_img_url: maskImageUrl.value,
        prompt: inpaintPrompt.value
      })
    })

    const data = await response.json()
    if (data.task_id) {
      pollInpaintResult(data.task_id)
    }  else {
      taskStatus.value = '重绘败: ' + data.error.details.join(', ')
      loading.value = false
    }
  } catch (error) {
    console.error('重绘失败:', error)
    taskStatus.value = '重绘失败: ' + error.message
    loading.value = false
  }
}

// 轮询结果函数
async function pollEraseResult(taskId) {
  try {
    const response = await fetch(`/mhapi/v1/erase/result/${taskId}`, {
      headers: {
        'Authorization': `Bearer ${token.value}`
      }
    })
    const data = await response.json()
    
    handleTaskResult(data, pollEraseResult.bind(null, taskId))
  } catch (error) {
    handlePollError(error)
  } finally {
    loading.value = false
  }
}

async function pollOutpaintResult(taskId) {
  try {
    const response = await fetch(`/mhapi/v1/outpaint/result/${taskId}`, {
      headers: {
        'Authorization': `Bearer ${token.value}`
      }
    })
    const data = await response.json()
    
    handleTaskResult(data, pollOutpaintResult.bind(null, taskId))
  } catch (error) {
    handlePollError(error)
  }
}

async function pollStylizeResult(taskId) {
  try {
    const response = await fetch(`/mhapi/v1/stylization/result/${taskId}?get_type=url`, {
      headers: {
        'Authorization': `Bearer ${token.value}`
      }
    })
    const data = await response.json()
    
    handleTaskResult(data, pollStylizeResult.bind(null, taskId))
  } catch (error) {
    handlePollError(error)
  }
}

async function pollInpaintResult(taskId) {
  try {
    const response = await fetch(`/mhapi/v1/inpaint/result/${taskId}`, {
      headers: {
        'Authorization': `Bearer ${token.value}`
      }
    })
    const data = await response.json()
    
    handleTaskResult(data, pollInpaintResult.bind(null, taskId))
  } catch (error) {
    handlePollError(error)
  }
}

// 处理任务结果
function handleTaskResult(data, pollFunction) {
  if (data.state === 'SUCCESS') {
    resultImage.value = data.images[0].raw
    taskStatus.value = '处理完成'
    loading.value = false
  } else if (data.state === 'FAILED') {
    taskStatus.value = '处理失败: ' + data.state_message
    loading.value = false
  } else {
    setTimeout(pollFunction, 1000)
  }
}

// 处理轮询错误
function handlePollError(error) {
  console.error('查询任务状态失败:', error)
  taskStatus.value = '查询任务状态失败: ' + error.message
  loading.value = false
}

// 组件加载时获取token和模型列表
onMounted(async () => {
  await getToken()
  await fetchAllModels()
})

// 添加遮罩图片处理方法
async function handleMaskInput() {
  if (!maskImageUrl.value) return
  maskImagePreview.value = maskImageUrl.value
}

function selectMaskImage(url) {
  maskImageUrl.value = url
  handleMaskInput()
}

// 修改 openMaskModal 方法
function openMaskModal() {
  if (!imageUrl.value) {
    alert('请先上传原图')
    return
  }
  showMaskModal.value = true
  nextTick(() => {
    initCanvas()
  })
}

function closeMaskModal() {
  showMaskModal.value = false
}

function initCanvas() {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.onload = () => {
    originalImage.value = img
    const canvas = canvasRef.value
    canvas.width = img.width
    canvas.height = img.height
    
    const ctx = canvas.getContext('2d')
    // 绘制原图
    ctx.drawImage(img, 0, 0)
    // 设置半透明白色遮罩
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    drawingContext.value = ctx
  }
  img.src = imageUrl.value
}

function startDrawing(event) {
  isDrawing.value = true
  const ctx = drawingContext.value
  ctx.beginPath()
  const rect = canvasRef.value.getBoundingClientRect()
  const scale = canvasRef.value.width / rect.width
  ctx.moveTo(
    (event.clientX - rect.left) * scale,
    (event.clientY - rect.top) * scale
  )
}

function draw(event) {
  if (!isDrawing.value) return
  
  const ctx = drawingContext.value
  const rect = canvasRef.value.getBoundingClientRect()
  const scale = canvasRef.value.width / rect.width
  
  ctx.fillStyle = 'black'
  ctx.strokeStyle = 'black'
  ctx.lineWidth = brushSize.value * scale
  ctx.lineCap = 'round'
  
  ctx.lineTo(
    (event.clientX - rect.left) * scale,
    (event.clientY - rect.top) * scale
  )
  ctx.stroke()
}

function stopDrawing() {
  isDrawing.value = false
  drawingContext.value?.closePath()
}

function clearMask() {
  initCanvas()
}

function saveMask() {
  const canvas = canvasRef.value
  const ctx = canvas.getContext('2d')
  
  // 创建新画布保存遮罩
  const maskCanvas = document.createElement('canvas')
  maskCanvas.width = canvas.width
  maskCanvas.height = canvas.height
  const maskCtx = maskCanvas.getContext('2d')
  
  // 获取原画布的图像数据
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data
  
  // 创建新的图像数据
  const maskImageData = maskCtx.createImageData(canvas.width, canvas.height)
  const maskData = maskImageData.data
  
  // 处理像素数据，保留黑色部分并设置白色背景
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 0) {
      // 保留黑色部分
      maskData[i] = 0; // Set to black
      maskData[i + 1] = 0; // Set to black
      maskData[i + 2] = 0; // Set to black
      maskData[i + 3] = 255; // Fully opaque
    } else {
      // 设置白色背景
      maskData[i] = 255; // Set to white
      maskData[i + 1] = 255; // Set to white
      maskData[i + 2] = 255; // Set to white
      maskData[i + 3] = 255; // Fully opaque
    }
  }
  
  maskCtx.putImageData(maskImageData, 0, 0)
  
  // 将 canvas 转换为 Blob 对象
  maskCanvas.toBlob((blob) => {
    // 生成对象 URL
    const objectURL = URL.createObjectURL(blob);
    
    // 将对象 URL 赋值给输入框
    // maskImageUrl.value = objectURL;
    maskImagePreview.value = objectURL;
  }, 'image/png', 1.0);
  
  // 关闭模态框
  closeMaskModal()
}
</script>

<template>
  <div class="video-gen-container">
    <div class="video-gen">
      <div class="section">
        <h2>场景补充</h2>
        
        <div class="tabs">
          <button 
            v-for="tab in ['erase', 'inpaint', 'outpaint', 'stylize', ]" 
            :key="tab"
            :class="{ active: currentTab === tab }"
            @click="currentTab = tab"
          >
            {{ {
              erase: '智能擦除',
              inpaint: '局部重绘',
              outpaint: '智能扩图',
              stylize: '风格化',
              
            }[tab] }}
          </button>
        </div>

        <div class="control-panel">
          <!-- 模型选择 -->
          <div class="input-group">
            <label>选择模型（必选）:</label>
            <select v-model="selectedModel">
              <option value="">请选择模型</option>
              <option 
                v-for="model in currentModels" 
                :key="model.id" 
                :value="model.id"
              >
                {{ model.name }}
              </option>
            </select>
            <span class="model-desc" v-if="selectedModel && currentModels.find(m => m.id === selectedModel)">
              {{ currentModels.find(m => m.id === selectedModel).description }}
            </span>
          </div>

          <!-- 主图片上传 -->
          <div class="input-group">
            <label>上传图片（必选）:</label>
            <div class="url-input-container">
              <input 
                type="text"
                v-model="imageUrl"
                placeholder="请输入图片URL"
                @change="handleImageInput"
                class="url-input"
              >
              <div class="source-controls">
                <input 
                  type="text" 
                  v-model="sourcePrefix"
                  placeholder="输入素材库前缀"
                  class="prefix-input"
                >
                <button 
                  @click="loadSourceImages"
                  :disabled="loading"
                  class="source-button"
                >
                  加载素材
                </button>
              </div>
            </div>
            
            <!-- 素材预览网格 -->
            <div v-if="sourceUrls.length" class="source-grid">
              <div 
                v-for="(url, index) in sourceUrls" 
                :key="index" 
                class="source-item"
                @click="selectSourceImage(url, 'main')"
              >
                <img :src="url" :alt="`素材 ${index + 1}`">
              </div>
            </div>

            <div class="preview" v-if="imagePreview">
              <img :src="imagePreview" class="preview-image" alt="预览图">
              <div class="image-dimensions" v-if="imageDimensions.width && imageDimensions.height">
                原图尺寸: {{ imageDimensions.width }} × {{ imageDimensions.height }} 像素
              </div>
            </div>
          </div>

          <!-- 遮罩图片上传 -->
          <div class="input-group" v-if="currentTab === 'erase' || currentTab === 'inpaint'">
            <label>遮罩图片（必选）:</label>
            <div class="url-input-container">
              <input 
                type="text"
                v-model="maskImageUrl"
                placeholder="请输入遮罩图片URL或使用绘制功能"
                @change="handleMaskInput"
                class="url-input"
              >
              <div class="mask-controls">
                <button 
                  @click="openMaskModal"
                  class="source-button"
                >
                  绘制遮罩
                </button>
                <a 
                  href="https://www.ejiaimg.cn/upload"
                  target="_blank"
                  class="source-link"
                  @click="window.open('https://www.ejiaimg.cn/upload', '_blank', 'width=800,height=600')"
                >
                  图床链接
                </a>
              </div>
            </div>
            <div class="preview" v-if="maskImagePreview">
              <img :src="maskImagePreview" class="preview-image" alt="遮罩预览图">
            </div>
          </div>

          <!-- 重绘提示词 -->
          <div class="input-group" v-if="currentTab === 'inpaint'">
            <label>提示词（必选）:</label>
            <textarea 
              v-model="inpaintPrompt"
              placeholder="请输入重绘提示词"
            ></textarea>
          </div>

          <!-- 扩图参数 -->
          <div class="input-group" v-if="currentTab === 'outpaint'">
            <label>扩图参数（必选）:</label>
            <div class="outpaint-controls">
              <div class="radio-group">
                <label>
                  <input 
                    type="radio" 
                    v-model="outpaintParams.useCustomSize"
                    :value="false"
                  > 等比例扩图
                </label>
                <label>
                  <input 
                    type="radio" 
                    v-model="outpaintParams.useCustomSize"
                    :value="true"
                  > 自由尺寸
                </label>
              </div>
              
              <div v-if="!outpaintParams.useCustomSize" class="ratio-input">
                <label>扩图比例:</label>
                <input 
                  type="number" 
                  v-model.number="outpaintParams.outpaint_ratio"
                  step="0.1"
                  min="1"
                  max="2"
                >
                <span class="helper-text">支持 1.0-2.0 之间的比例</span>
              </div>
              
              <div v-else class="size-inputs">
                <div class="input-with-label">
                  <label>宽度:</label>
                  <input 
                    type="number" 
                    v-model.number="outpaintParams.width"
                    placeholder="宽度"
                    min="64"
                    step="64"
                  >
                </div>
                <div class="input-with-label">
                  <label>高度:</label>
                  <input 
                    type="number" 
                    v-model.number="outpaintParams.height"
                    placeholder="高度"
                    min="64"
                    step="64"
                  >
                </div>
              </div>
            </div>
          </div>

          <button 
            @click="
              currentTab === 'erase' ? runErase() :
              currentTab === 'outpaint' ? runOutpaint() :
              currentTab === 'stylize' ? runStylize() :
              runInpaint()
            "
            :disabled="loading"
            class="primary-button"
          >
            {{ loading ? '处理中...' : '开始处理' }}
          </button>
        </div>

        <pre class="status-log" v-if="taskStatus">{{ taskStatus }}</pre>

        <!-- 结果展示 -->
        <div class="result-image" v-if="resultImage">
          <h3>处理结果</h3>
          <img :src="resultImage" alt="处理结果">
        </div>
      </div>
    </div>
  </div>

  <!-- 遮罩绘制模态框 -->
  <div class="modal" v-if="showMaskModal">
    <div class="modal-content">
      <div class="modal-header">
        <h3>绘制遮罩</h3>
        <button class="close-button" @click="closeMaskModal">&times;</button>
      </div>
      <div class="brush-controls">
        <label>笔刷大小:</label>
        <input 
          type="range" 
          v-model.number="brushSize"
          min="1"
          max="50"
          step="1"
          class="brush-size-slider"
        >
        <span class="brush-size-value">{{ brushSize }}px</span>
      </div>
      <div class="canvas-container">
        <canvas
          ref="canvasRef"
          @mousedown="startDrawing"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseleave="stopDrawing"
        ></canvas>
      </div>
      <div class="modal-footer">
        <button class="source-button" @click="clearMask">
          重置
        </button>
        <button class="primary-button" @click="saveMask">
          结束
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.video-gen-container {
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  box-sizing: border-box;
  border-radius: 16px;
}

.video-gen {
  margin: 0 auto;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 15px 50px rgba(0,0,0,0.2);
  backdrop-filter: blur(5px);
  animation: fadeIn 0.5s ease-out;
}

.tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  background: var(--section-bg);
  padding: 10px;
  border-radius: 8px;
}

.tabs button {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  background: var(--card-bg);
  color: var(--text-color);
  cursor: pointer;
  transition: all 0.3s ease;
}

.tabs button.active {
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.section {
  margin-bottom: 30px;
  padding: 25px;
  background: var(--section-bg);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
}

.control-panel {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.size-inputs {
  display: flex;
  gap: 10px;
}

.size-inputs input {
  flex: 1;
  padding: 8px;
  border: 2px solid var(--border-color);
  border-radius: 6px;
}

.model-desc {
  font-size: 0.9em;
  color: var(--subtitle-color);
  margin-top: 4px;
  font-style: italic;
}

.primary-button {
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  align-self: flex-start;
}

.primary-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px var(--focus-shadow);
}

select, textarea {
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  background: var(--input-bg);
  color: var(--text-color);
}

select {
  cursor: pointer;
}

select:hover {
  border-color: var(--primary-color);
}

select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-shadow);
}

.url-input-container {
  width: 100%;
  margin-bottom: 10px;
}

.url-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  background: var(--input-bg);
  color: var(--text-color);
}

.url-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-shadow);
}

.preview {
  margin-top: 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 10px;
  background: var(--input-bg);
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: contain;
}

.source-controls {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.prefix-input {
  flex: 1;
  padding: 8px 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  background: var(--input-bg);
  color: var(--text-color);
}

.source-button {
  white-space: nowrap;
  padding: 8px 16px;
  background: var(--card-bg);
  color: var(--text-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0.9;
}

.source-button:hover {
  background: var(--primary-color);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.source-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 15px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  background: var(--section-bg);
  border-radius: 8px;
}

.source-item {
  aspect-ratio: 1;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.source-item:hover {
  border-color: var(--primary-color);
  transform: scale(1.05);
}

.source-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.result-image {
  margin-top: 20px;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.result-image h3 {
  margin-bottom: 15px;
  color: var(--text-color);
}

.result-image img {
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 4px var(--card-shadow);
}

.status-log {
  margin: 15px 0;
  padding: 15px;
  background: var(--log-bg);
  color: var(--log-text);
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  max-height: 150px;
  overflow-y: auto;
  font-size: 0.9em;
  line-height: 1.5;
  border: 1px solid var(--border-color);
}

.outpaint-controls {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px;
  background: var(--section-bg);
  border-radius: 8px;
}

.radio-group {
  display: flex;
  gap: 20px;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.ratio-input {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ratio-input input {
  width: 100px;
  padding: 8px;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
}

.helper-text {
  font-size: 0.9em;
  color: var(--subtitle-color);
}

.size-inputs {
  display: flex;
  gap: 20px;
}

.input-with-label {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-with-label label {
  font-size: 0.9em;
  color: var(--text-color);
}

.size-inputs input {
  flex: 1;
  padding: 8px;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 20px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-color);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-button:hover {
  background: var(--section-bg);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.canvas-container {
  width: 100%;
  overflow: auto;
  background: var(--section-bg);
  border-radius: 8px;
  padding: 10px;
}

canvas {
  width: 100%;
  height: auto;
  cursor: crosshair;
}

.mask-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mask-status {
  color: var(--primary-color);
  font-size: 0.9em;
}

.brush-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background: var(--section-bg);
  border-radius: 8px;
}

.brush-size-slider {
  flex: 1;
  height: 6px;
  -appearance: none;
  background: var(--border-color);
  border-radius: 3px;
  outline: none;
  cursor: pointer;
}

.brush-size-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  background: var(--primary-color);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.brush-size-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}

.brush-size-value {
  min-width: 50px;
  text-align: right;
  color: var(--text-color);
  font-size: 0.9em;
}

.image-dimensions {
  margin-top: 8px;
  font-size: 0.9em;
  color: var(--subtitle-color);
  text-align: center;
}

.source-link {
  color: var(--primary-color);
  text-decoration: none;
  font-size: 14px;
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.source-link:hover {
  background: var(--primary-color);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
</style> 